import { Card } from '@frontend/ui'

const medals = ['\u{1F947}', '\u{1F948}', '\u{1F949}'] as const

interface StatCardProps {
  label: string
  value: string | number
  topPerformers?: { displayName: string; value: string | number }[]
  subtext?: string
  secondaryItems?: { label: string; value: string | number }[]
}

// Splits a label like "Total Distance (m)" into ["Total Distance", "(m)"].
// The unit suffix renders in muted-foreground to match the Figma design.
function splitLabel(label: string): { main: string; unit?: string } {
  const match = label.match(/^(.*?)\s*(\([^)]*\))\s*$/)
  if (!match) return { main: label }
  return { main: match[1], unit: match[2] }
}

export function StatCard({
  label,
  value,
  topPerformers,
  subtext,
  secondaryItems
}: StatCardProps) {
  const { main, unit } = splitLabel(label)
  return (
    <Card className='gap-2 p-4'>
      <div className='flex items-start gap-1 text-sm font-medium'>
        <span className='text-card-foreground'>{main}</span>
        {unit && <span className='text-muted-foreground'>{unit}</span>}
      </div>
      <div className='text-card-foreground text-2xl leading-8 font-bold tabular-nums'>
        {value}
      </div>
      {topPerformers && topPerformers.length > 0 && (
        <div className='flex flex-col gap-1 text-sm font-normal'>
          {topPerformers.map((p, i) => (
            <div
              key={p.displayName}
              className='flex items-center gap-2 text-sm'
            >
              <span className='shrink-0'>{medals[i] ?? ''}</span>
              <span className='text-card-foreground min-w-0 flex-1 truncate'>
                {p.displayName}
              </span>
              <span className='text-card-foreground shrink-0 tabular-nums'>
                {p.value}
              </span>
            </div>
          ))}
        </div>
      )}
      {subtext && (
        <span className='text-muted-foreground text-sm'>{subtext}</span>
      )}
      {secondaryItems && secondaryItems.length > 0 && (
        <div className='border-border flex flex-col gap-1 border-t pt-3'>
          {secondaryItems.map((item) => (
            <div
              key={item.label}
              className='flex items-center justify-between text-sm'
            >
              <span className='text-muted-foreground'>{item.label}</span>
              <span className='text-card-foreground font-medium tabular-nums'>
                {item.value}
              </span>
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}
